/* -------------------------- Design imports start -------------------------- */
import {
  TipsAndUpdatesRounded,
  CheckBoxRounded,
  ExpandMoreRounded,
  AddRounded,
  DeleteRounded,
  ArrowForwardRounded,
  InsertDriveFileRounded,
} from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Step,
  StepIcon,
  StepLabel,
  Stepper,
  TextField,
  ThemeProvider,
  Typography,
  createTheme,
} from '@mui/material'
import {
  type ChangeEvent,
  type ReactElement,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'

import { type GuestRequest } from '../../utils/types'
import Button from '../../../../components/Button'
import InfoField from '../../../../components/inputs/InfoField'
import NumberInputWithUnit from '../../../../components/inputs/NumberInputWithUnit'
import MultilevelDrawer from '../../../../components/layout/MultilevelDrawer'

import FieldLabel from '../../../../components/widgets/FieldLabel'
import InteractiveTextDisplay from '../../../../components/widgets/InteractiveTextDisplay'
import LogTool from '../../../../logger/logTools'
import { getPKfromSelf, missingRequiredFormFields } from '../../../../utils/functions'
import { Contact, ContactGroup, type FileInfo, type SelectOption } from '../../../../utils/types'
import { useCreateArticle } from '../../../Item/hooks/useArticleMutations'
import { useFeedbackFormHandler } from '../../../FormEditor/hooks/useFeedbackFormHandler'
import { useContactHandler } from '../../../FormEditor/hooks/useContactHandler'
import { useManualSharesHandler } from '../../../FormEditor/hooks/useManualSharesHandler'
import {
  useAutomaticGroupSharesHandler,
  useAutomaticSharesHandler,
} from '../../../FormEditor/hooks/useAutomaticSharesHandler'
import { useSharepointDurationHandler } from '../../../FormEditor/hooks/useSharepointDurationHandler'
import { useSharepointCreateHandler } from '../../../FormEditor/hooks/useSharepointConfirmHandlers'
import { apiClient } from '../../../../api/client'
import FormEditorDrawer from '../../../FormEditor/components/FormEditorDrawer'
import SearchSelectInput from '../../../../components/inputs/SearchSelectInput'
/* --------------------------- Design imports end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */

import ArticleDrawer from '../../../Item/ArticleDrawer'
import {
  createArticle,
  createSharepoint,
  getArticleRepresentation,
} from '../../../Item/utils/functions'
import { useAllArticles } from '../../../Item/hooks/useArticleQueries'
import {
  inputToAPIArticleJSON,
  inputToAPIFeedbackFormJSON,
  inputToAPISharepointJSON,
} from '../../../Item/api/articleApi'
import { type Article, type FeedbackForm, type Sharepoint } from '../../../Item/utils/types'

import { Form, FormObject, type FormFieldType } from '../../../Form'
import { useUserContext } from '../../../../utils/context'
import GetConstant from '../../utils/constants'

import { type Supplier } from '../../../Supplier/utils/types'
import { type Customer } from '../../../Customer/utils/types'
import ContactDrawer from '../../../Customer/ContactDrawer'
import { FilesDrawer, getFileExtension } from '../../../Filesystem'
import ImageManipulationModal from '../../../../components/imageManipulation/ImageManipulationModal'
import ArticleForm from '../../../Item/ArticleForm'
import { uploadFileToUrl } from '../../../Filesystem/api/fileApi'
import {
  generateAddFormFieldButton,
  generateAddSectionTitleButton,
  generateSectionTitle,
  generateTextField,
} from '../../../FormEditor/utils/functions'
import { EditorFieldObject } from '../../../FormEditor/utils/types'
import useUniqueKey from '../../../../hooks/useUniqueKey'
import ContactGroupDrawer from '../../../Contact/components/ContactGroupDrawer'
import { useContactGroupHandler } from '../../../FormEditor/hooks/useContactGroupHandler'
import { buildFeedbackFormSuggestion } from '../../../Sharepoint/utils/functions'
/* ------------------------- Functional imports end ------------------------- */

type Props = {
  open: boolean
  setOpen: (open: boolean) => void
  formState: {
    state: any | undefined
    setState: React.Dispatch<React.SetStateAction<any | undefined>>
  }
  selectedGuestRequest: GuestRequest
  onClose?: () => void
  onConfirm?: (input: any) => Promise<Sharepoint | 'ERROR'>
}

type AutomaticShareFormInput = {
  [inputKey: string]: { contact: SelectOption; customInvitationMessage: string }
}

type ManualShareInput = {
  comment: string
  password?: string
  accessUrl?: string
}

/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */

export default function CreateGuestRequestSharepointDrawer(props: Props): ReactElement {
  /* -------------------------- Non state data start -------------------------- */
  const {
    open,
    setOpen,
    formState,
    selectedGuestRequest,
    onClose = () => null,
    onConfirm = () => null,
  } = props
  const log = new LogTool({
    context: 'CreateGuestRequestSharepointDrawer',
    enable: true,
    logLevel: 'debug',
  })
  const { t } = useTranslation()
  const { user } = useUserContext()
  const isGuestUser = user?.isGuestUser ?? false
  const history = useHistory()
  const securePasswordGenerator = require('secure-random-password')
  const uniqueFieldKey = useUniqueKey('inputField')
  const createArticleMutation = useCreateArticle()

  const defaultShareDuration = '20'

  const customTheme = createTheme({
    components: {
      MuiStepIcon: {
        styleOverrides: {
          root: {
            '&.Mui-active': {
              color: '#16a9e4',
            },
            '&.Mui-completed': {
              color: '#16a9e4',
            },
          },
          text: {
            color: 'white', // Set the text color to white for all steps
          },
        },
      },
    },
  })
  /* --------------------------- Non state data end --------------------------- */

  /* ---------------------------- Flag states start --------------------------- */
  const [prevOpen, setPrevOpen] = useState(false)
  const [waitingOnAction, setWaiting] = useState(false)
  // this form-state is purely for frontend purposes. In the backend unlimited
  // sharepoint duration is set by setting the sharepoint duration to 0
  const [openFormEditorDrawer, setOpenFormEditorDrawer] = useState(false)
  const [openContactDrawer, setOpenContactDrawer] = useState(false)
  const [openContactGroupDrawer, setOpenContactGroupDrawer] = useState(false)
  const [openArticleDrawer, setOpenArticleDrawer] = useState(false)
  const [openFilesDrawer, setOpenFilesDrawer] = useState(false)
  const [openImageEditor, setOpenImageEditor] = useState(false)
  /* ----------------------------- Flag states end ---------------------------- */

  /* ---------------------------- Data states start --------------------------- */
  const uniqueKeyStore = useRef(0)
  const articleStore = useRef<Article[]>([])
  const [articleOptions, setArticleOptions] = useState<SelectOption[]>()
  const [activeStep, setActiveStep] = useState<number>(0)
  const [articleFormInput, setArticleFormInput] = useState<any>({
    description: selectedGuestRequest.message as string,
    material: selectedGuestRequest.material,
  })
  const [articleFileInput, setArticleFileInput] = useState<
    (FileInfo & { blob?: Blob })[] | undefined
  >(undefined)
  const [selectedArticleFile, setSelectedArticleFile] = useState<FileInfo | undefined>(undefined)
  const [feedbackFormPreviewInput, setFeedbackFormPreviewInput] = useState<any>()
  /* ----------------------------- Data states end ---------------------------- */

  /* ------------------------------ Effects start ----------------------------- */
  // Disable rich contacts query for guest users - they don't have access to these resources
  // Use feedback form handler hook for all feedback form related logic
  const {
    feedbackFormOptions,
    formEditorInput,
    setFormEditorInput,
    handleAddFormOption,
    getFeedbackForm,
  } = useFeedbackFormHandler(formState)

  // Use manual shares handler hook for all manual shares related logic
  const {
    manualSharesForm,
    setManualSharesForm,
    manualSharesFormInput,
    setManualSharesFormInput,
    generateManualShareFieldObject,
    handleAddManualShare,
    handleDeleteManualShare,
    missingManualShareComment,
    resetManualShares,
  } = useManualSharesHandler(t, log, formState, securePasswordGenerator, false)

  // Use automatic shares handler hook for all automatic shares related logic
  const {
    automaticSharesForm,
    setAutomaticSharesForm,
    automaticSharesFormInput,
    setAutomaticSharesFormInput,
    generateAutomaticShareFieldObject,
    handleAddAutomaticShare,
    handleDeleteAutomaticShare,
    missingAutomaticShareSelect,
    customInvitationMessageTooLong,
    resetAutomaticShares,
  } = useAutomaticSharesHandler(t, log, false)

  const {
    automaticGroupSharesForm,
    setAutomaticGroupSharesForm,
    automaticGroupSharesFormInput,
    setAutomaticGroupSharesFormInput,
    generateAutomaticGroupShareFieldObject,
    handleAddAutomaticGroupShare,
    handleDeleteAutomaticGroupShare,
    missingAutomaticGroupShareSelect,
    customGroupInvitationMessageTooLong,
    resetAutomaticGroupShares,
  } = useAutomaticGroupSharesHandler(t, log, false)

  // Use contact handler hook for all contact related logic
  const {
    contactStore,
    contactOptions,
    contactFormInput,
    setContactFormInput,
    newContactReceiver,
    setNewContactReceiver,
    handleAddContactOption,
  } = useContactHandler(isGuestUser, setAutomaticSharesFormInput)

  const {
    contactGroupStore,
    contactGroupOptions,
    contactGroupFormInput,
    setContactGroupFormInput,
    newGroupReceiver,
    setNewGroupReceiver,
    handleAddContactGroupOption,
  } = useContactGroupHandler(isGuestUser, setAutomaticGroupSharesFormInput)

  // Use sharepoint duration handler hook for all sharepoint duration related logic
  const { unlimitedShareDuration, setUnlimitedShareDuration, resetSharepointDuration } =
    useSharepointDurationHandler(false)
  const { runCreateFlow } = useSharepointCreateHandler(setWaiting)

  /* ------------------------------- Effects end ------------------------------ */

  /* ------------------------- Utility functions start ------------------------ */
  /**
   * Check if all required FormFields are filled in correctly.
   * @returns
   */
  const isValidArticleInput = (): boolean => {
    // Get required fields: Material and Number
    const requiredFields: FormFieldType[] = [
      {
        key: 'material',
        required: true,
        validation: true,
        type: 'select-search',
        label: t('item:content.label.material'),
      },
      {
        key: 'number',
        required: true,
        validation: true,
        type: 'text',
        label: t('common:content.label.articleNumber'),
      },
    ]
    return !missingRequiredFormFields(requiredFields, articleFormInput)
  }
  /* -------------------------- Utility functions end ------------------------- */

  /* ------------------------ Callback functions start ------------------------ */
  const handleClose = (preventOnClose = false) => {
    // reset drawer to initial state
    uniqueKeyStore.current = 0
    setPrevOpen(false)
    setActiveStep(0)
    resetManualShares()
    resetAutomaticShares()
    resetSharepointDuration()
    setFormEditorInput(undefined)
    setFeedbackFormPreviewInput({})
    formState.setState({})

    setWaiting(false)

    setOpen(false)

    !preventOnClose && onClose()
  }

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(prev => prev - 1)
    }
  }

  const handleNext = () => {
    if (activeStep < steps.length) {
      setActiveStep(prev => prev + 1)
    }
  }

  const handleConfirm = async () => {
    try {
      // collect automatic shares from single shares and group shares
      const collectedAutomaticShares: { contact: string; customInvitationMessage?: string }[] = []
      // collect contacts from group shares
      if (
        automaticGroupSharesForm.length > 1 &&
        Object.values(automaticGroupSharesFormInput).length > 0
      ) {
        Object.values(automaticGroupSharesFormInput).forEach(automaticGroupShare => {
          const instance = contactGroupStore.current.find(
            group => group.self === automaticGroupShare.group.value
          )
          collectedAutomaticShares.push(
            ...(instance?.contacts as Contact[]).map(contact => ({
              contact: contact.self,
              customInvitationMessage: automaticGroupShare.customInvitationMessage,
            }))
          )
        })
      }
      // collect contacts from single shares
      if (automaticSharesForm.length > 1 && Object.values(automaticSharesFormInput).length > 0) {
        collectedAutomaticShares.push(
          ...Object.values(automaticSharesFormInput).map(automaticShare => ({
            contact: automaticShare.contact.value,
            customInvitationMessage: automaticShare.customInvitationMessage,
          }))
        )
      }
      // remove duplicates from automatic shares
      const seenAutomaticShares: Set<string> = new Set()
      const dedupedAutomaticShares = collectedAutomaticShares.filter(automaticShare => {
        if (seenAutomaticShares.has(automaticShare.contact)) return false
        seenAutomaticShares.add(automaticShare.contact)
        return true
      })
      const payload = inputToAPISharepointJSON({
        ...formState.state,
        shareDuration: unlimitedShareDuration
          ? '0'
          : (formState.state.shareDuration ?? defaultShareDuration),
        articles: [formState.state.guestRequestArticle.value],
        guestRequest: selectedGuestRequest.self,
        ...(formState.state.feedbackForm && { feedbackForm: formState.state.feedbackForm.value }),
        ...(collectedAutomaticShares && { automaticShares: dedupedAutomaticShares }),
        // ...(formState.state.automaticShares && {
        //   // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        //   automaticShares: formState.state.automaticShares.map(
        //     (supplier: SelectOption) => supplier.value
        //   ),
        // }),
        // ...(automaticSharesForm.length > 1 &&
        //   Object.values(automaticSharesFormInput).length > 0 && {
        //     // convert the formInput object into a list of automatic share info-objects
        //     automaticShares: Object.values(automaticSharesFormInput).map(automaticShare => ({
        //       contact: automaticShare.contact.value,
        //       customInvitationMessage: automaticShare.customInvitationMessage,
        //     })),
        //   }),
        ...(manualSharesForm.length > 1 &&
          Object.values(manualSharesFormInput).length > 0 && {
            // convert the formInput object into a list of manual share info-objects
            manualShares: Object.values(manualSharesFormInput),
          }),
      })

      const sharepoint = await runCreateFlow(payload)
      toast.success(t('sharepoint:feedback.success.creatingSharepoint'))
      // go to next step (does not really exist) to display the generated sharepoint url
      try {
        const response = await apiClient.get(sharepoint.self + 'manual_shares/')
        log.info('Success fetching manual shares (GuestUsers)', response.results)
        // combine all manual shares data from user input and server response
        setManualSharesFormInput(
          response.results.map((manualShareServerResp: any) => {
            // compare manual share from server response with the manual shares created within this drawer
            const match: any = Object.values(manualSharesFormInput).find(
              (manualShareInput: any) => manualShareInput.comment === manualShareServerResp.comment
            )
            return {
              comment: match?.comment,
              password: match?.password,
              // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
              accessUrl: window.location.origin + '/guest-signIn/' + manualShareServerResp.guest_id,
            }
          })
        )
        if (Object.keys(manualSharesFormInput).length > 0) {
          handleNext()
        } else {
          handleClose(true)
        }
      } catch (error) {
        log.error('Error fetching manual shares:', error)
      }
    } catch (error) {
      log.error('Error creating sharepoint', error)
      toast.error(t('sharepoint:feedback.error.creatingSharepoint'))
    }
  }

  const handleAddArticleOption = async (articleInput: any): Promise<any> => {
    log.info('Begin creating article option')
    try {
      const response = await createArticleMutation.mutateAsync(articleInput)
      log.info('Success creating article option')

      // update states
      const article = createArticle(response)
      articleStore.current.push(article)
      setArticleOptions(prev => [
        ...prev!,
        { value: article.self, label: getArticleRepresentation(article) },
      ])
      formState.setState((prev: any) => ({
        ...prev,
        guestRequestArticle: { value: article.self, label: getArticleRepresentation(article) },
      }))

      return response.files
    } catch (error) {
      log.error('Error creating article option:', error)
      return 'ERROR'
    }
  }

  const handleConfirmImageManipulation = (
    filename: string,
    exportedImage: Blob
  ): 'SUCCESS' | 'ERROR' => {
    // insert the blob into the articleFileInput state
    setArticleFileInput(prev => {
      if (!prev) return undefined
      const i = prev.findIndex(file => file.key && file.key === selectedArticleFile!.key)
      prev[i].name = filename + `.${getFileExtension(prev[i].name!)}`
      prev[i].blob = exportedImage
      return prev
    })
    // reset drawer state
    setSelectedArticleFile(undefined)

    return 'SUCCESS'
  }

  const handleCreateAnonymizedArticle = async (articleInput: any): Promise<void> => {
    if (!isValidArticleInput()) throw new Error('Cannot create article, input is invlaid.')
    log.info('Begin creating anonymized article')
    try {
      const response = await createArticleMutation.mutateAsync(articleInput)
      const article = createArticle(response)
      log.info('Success creating anonymized article')

      // check if the user specified files to upload !
      if (articleFileInput?.length && articleFileInput.length > 0) {
        const articleFilesUrl = article.self + 'files/'
        // upload the files one by one for a more detailed feedback if something goes wrong,
        // and to make use parallel uploading to decrease upload time
        for (const file of articleFileInput) {
          if (!(file instanceof File)) {
            // to upload the file it is required to be a File object
            if (file.blob) {
              // the file has been edited and we have the contents available as blob
              void uploadFileToUrl(articleFilesUrl, new File([file.blob], file.name!))
            } else {
              // we currently do not have the file contents available -> download the file
              // and create a File object from the response.blob()
              const response = await fetch(file.document as string)
              if (!response.ok) {
                log.error(
                  `Error loading ${file.name}. This file will not be included in the anonymized article.`
                )
                // continue with next file
                continue
              }
              const blob = await response.blob()
              void uploadFileToUrl(articleFilesUrl, new File([blob], file.name!))
            }
          }
        }
      }

      // update states
      articleStore.current.push(article)
      formState.setState((prev: any) => ({
        ...prev,
        guestRequestArticle: { value: article.self, label: getArticleRepresentation(article) },
      }))
    } catch (error) {
      log.error('Error creating anonymized article:', error)
      toast.error(t('item:feedback.error.creatingArticle'))
    }
  }
  /* ------------------------ Callback functions end ------------------------ */

  /* ------------------- Custom FormField Components start ------------------- */
  const ArticleListComponent = () => (
    <>
      <FieldLabel value={t('common:content.label.article')} />
      <List
        sx={{
          maxHeight: '20vh',
          overflowY: 'auto',
          overflowX: 'hidden',
          border: '1px solid lightGrey',
          borderRadius: '4px',
        }}
      >
        <ListItem key={getPKfromSelf(formState.state.guestRequestArticle.self)}>
          <ListItemIcon>
            <CheckBoxRounded color="disabled" />
          </ListItemIcon>
          <ListItemText
            sx={{
              textWrap: 'wrap',
              wordBreak: 'break-word',
            }}
            primary={getArticleRepresentation(selectedArticle)}
          />
        </ListItem>
      </List>
    </>
  )

  const DurationInputComponent = () => (
    <FormControl fullWidth>
      <NumberInputWithUnit
        disabled={unlimitedShareDuration}
        label={t('sharepoint:content.label.sharepointDuration')}
        value={
          unlimitedShareDuration ? '' : (formState.state.shareDuration ?? defaultShareDuration)
        }
        limits={{ minInput: 1, maxInput: 365 }}
        unit={t('common:content.label.days')}
        onChange={function (value: string | number): void {
          formState.setState((prev: any) => {
            return {
              ...prev,
              shareDuration: value,
            }
          })
        }}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={unlimitedShareDuration}
            onChange={(e: any) => setUnlimitedShareDuration(e.target.checked)}
          />
        }
        label={t('sharepoint:interaction.checkbox.unlimitedSharepointDuration')}
      />
    </FormControl>
  )

  const ArticleDescriptionComponent = () => (
    <Typography variant="body1">
      {t('sharepoint:content.description.articleSharepointHints.sharedArticlesHint')}
    </Typography>
  )

  const DurationDescriptionComponent = () => (
    <Typography variant="body1">
      {t('sharepoint:content.description.configureSharepoint.durationHint')}
    </Typography>
  )
  /* -------------------- Custom FormField Components end -------------------- */

  /* ------------------------ Render constants start ------------------------ */
  const CustomStepIcon = (props: any) => {
    return (
      <ThemeProvider theme={customTheme}>
        <StepIcon {...props} />
      </ThemeProvider>
    )
  }

  const steps = [
    t('common:content.label.hints'),
    t('item:content.label.createArticle'),
    t('sharepoint:content.label.configureSharepoint'),
    t('sharepoint:content.label.shareSharepoint'),
  ]

  const implicationsAndComments = [
    {
      content: (
        <span>
          {t('sharepoint:content.description.guestRequestSharepointHints.guestRequestArticleHint')}
        </span>
      ),
    },
    {
      content: (
        <>
          <span>
            {t('sharepoint:content.description.guestRequestSharepointHints.editSharepointHint')}
          </span>
          <br />
          <Button
            variant="contained"
            color="inherit"
            sx={{ mt: '0.5rem' }}
            onClick={() => history.push('/items/sharepoints')}
          >
            {t('sharepoint:interaction.button.editSharepoint')}
          </Button>
        </>
      ),
    },
  ]

  const renderDrawerHeader = () => {
    return (
      <Box display="flex" width="100%">
        <Typography variant="h6" noWrap sx={{ flexShrink: 0, mr: '2rem' }}>
          Gastanfrage teilen
        </Typography>
        <Stepper
          activeStep={activeStep}
          sx={{ marginRight: '16px', marginLeft: '-8px', width: '100%' }}
        >
          {steps.map((label: string, index: number) => {
            return (
              <Step key={index}>
                <StepLabel StepIconComponent={CustomStepIcon}>{label}</StepLabel>
              </Step>
            )
          })}
        </Stepper>
      </Box>
    )
  }

  const renderDrawerActions = () => {
    return (
      <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
        {/* ------------------- Start cancel / back buttons ------------------ */}
        {activeStep === 0 && (
          // show cancel-button on first step and after creating the sharepoint
          <Button
            id="cancelButton"
            onClick={() => setOpen(false)}
            color="inherit"
            variant="contained"
            disabled={waitingOnAction}
            sx={{ mr: 1 }}
          >
            {t('common:interaction.button.cancel')}
          </Button>
        )}
        {activeStep > 0 && activeStep < steps.length && (
          <Button
            id="backButton"
            color="inherit"
            variant="contained"
            disabled={waitingOnAction}
            onClick={handleBack}
            sx={{ mr: 1 }}
          >
            {t('common:interaction.button.back')}
          </Button>
        )}
        {/* ------------------- End cancel / back buttons ------------------ */}

        {/* ----------------- Start next / confirm buttons ----------------- */}
        {activeStep < 3 && (
          <Button
            id="nextButton"
            variant="contained"
            disabled={activeStep === 1 && !formState.state.guestRequestArticle}
            sx={{ marginLeft: '10px' }}
            onClick={handleNext}
          >
            {t('common:interaction.button.next')}
          </Button>
        )}
        {activeStep === 3 && (
          <LoadingButton
            id="createButton"
            variant="contained"
            sx={{ textTransform: 'none', marginLeft: '10px', color: 'whitesmoke' }}
            // disabled={nextStepDisabled(activeStep)}
            loading={waitingOnAction}
            onClick={handleConfirm}
            disabled={
              // to create a sharepoint the user has to specify at least one sharepoint guest
              (Object.values(automaticGroupSharesFormInput).length === 0 &&
                Object.values(automaticSharesFormInput).length === 0 &&
                Object.values(manualSharesFormInput).length === 0) ||
              missingAutomaticGroupShareSelect() ||
              missingAutomaticShareSelect() ||
              missingManualShareComment()
            }
          >
            {t('sharepoint:interaction.button.createSharepoint')}
          </LoadingButton>
        )}
        {activeStep === steps.length && (
          <Button
            id="doneButton"
            variant="contained"
            sx={{ textTransform: 'none', marginLeft: '10px', color: 'whitesmoke' }}
            onClick={() => handleClose(false)}
          >
            {t('common:interaction.button.done')}
          </Button>
        )}
        {/* ------------------ End next / confirm buttons ------------------ */}
      </Box>
    )
  }

  const renderFeedbackForm = (form: FeedbackForm | undefined) => {
    if (typeof form === 'undefined') {
      return (
        <Typography
          variant="body1"
          color="GrayText"
          textAlign="center"
          width="100%"
          mt="1.5rem"
          mb="1.5rem"
        >
          {t('sharepoint:feedback.info.selectFeedbackForm')}
        </Typography>
      )
    }
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h5" textAlign="center" fontWeight={700} mb="2.5rem">
            {form.formTitle}
          </Typography>
        </Grid>
        <Form
          formObject={feedbackFormPreviewInput}
          formFields={form.formJson}
          onChange={(input: any) => setFeedbackFormPreviewInput({ ...input })}
          editing
        />
      </Grid>
    )
  }

  const renderAutomaticShareFieldObject = (automaticShareFieldObj: any, listItemKey: number) => {
    if (automaticShareFieldObj.type === 'addAutomaticShare') {
      return (
        <ListItem key={listItemKey}>
          <ListItemIcon>
            <Button
              startIcon={<AddRounded style={{ fontSize: '25px', color: 'GrayText' }} />}
              onClick={handleAddAutomaticShare}
              sx={{ fontSize: '16px', color: 'black', ml: '4px' }}
              style={{ color: 'black', textTransform: 'none' }}
            >
              {t('sharepoint:content.label.addAutomaticShare')}
            </Button>
          </ListItemIcon>
        </ListItem>
      )
    }
    return (
      <ListItem key={listItemKey} sx={{ alignItems: 'start' }}>
        <ListItemIcon sx={{ marginTop: '0.6875rem' }}>
          <IconButton onClick={() => handleDeleteAutomaticShare(automaticShareFieldObj)}>
            <DeleteRounded />
          </IconButton>
        </ListItemIcon>
        <ListItemText>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <SearchSelectInput
                name={t('sharepoint:content.label.shareWith')}
                fullWidth
                value={automaticSharesFormInput[automaticShareFieldObj.key]?.contact ?? null}
                // exclude all contacts that are selected already
                options={contactOptions.filter(
                  (option: SelectOption) =>
                    !Object.values(automaticSharesFormInput)
                      .map(
                        (autoShare: { contact: SelectOption; customInvitationMessage: string }) =>
                          autoShare.contact
                      )
                      .includes(option)
                )}
                onChange={(input: any) => {
                  const fieldObjKey = automaticShareFieldObj.key
                  log.debug('Updating', fieldObjKey, 'with value ->', input)
                  const selectedOption =
                    contactOptions.find(
                      (option: SelectOption) =>
                        (typeof input === 'object' && input?.value === option.value) ||
                        input === option.value
                    ) || (typeof input === 'object' && input?.value ? input : null)

                  if (!selectedOption) {
                    log.warn('Could not find matching option for input:', input)
                    return
                  }

                  setAutomaticSharesFormInput(prev => {
                    const newState = { ...prev }
                    if (!newState[fieldObjKey]) {
                      // first select change -> create input object
                      newState[fieldObjKey] = {
                        contact: selectedOption,
                        customInvitationMessage: '',
                      }
                    } else {
                      newState[fieldObjKey] = { ...newState[fieldObjKey], contact: selectedOption }
                    }
                    return newState
                  })
                }}
                onAddOption={() => {
                  setNewContactReceiver(automaticShareFieldObj.key)
                  setOpenContactDrawer(true)
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={
                  (
                    automaticSharesFormInput[automaticShareFieldObj.key]?.customInvitationMessage ??
                    ''
                  ).length > 0
                    ? 'Personalisierte Einladungsnachricht' +
                      `    ${(automaticSharesFormInput[automaticShareFieldObj.key]?.customInvitationMessage ?? '').length}/500`
                    : 'Personalisierte Einladungsnachricht'
                }
                value={
                  automaticSharesFormInput[automaticShareFieldObj.key]?.customInvitationMessage ??
                  ''
                }
                disabled={!automaticSharesFormInput[automaticShareFieldObj.key]?.contact}
                fullWidth
                multiline
                rows={2}
                error={
                  (
                    automaticSharesFormInput[automaticShareFieldObj.key]?.customInvitationMessage ??
                    ''
                  ).length > 500
                }
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  const fieldObjKey = automaticShareFieldObj.key
                  const input = event.target.value
                  log.debug('Updating', fieldObjKey, 'with value ->', input)

                  setAutomaticSharesFormInput(prev => {
                    prev[fieldObjKey] = { ...prev[fieldObjKey], customInvitationMessage: input }
                    return { ...prev }
                  })
                }}
              />
            </Grid>
          </Grid>
        </ListItemText>
      </ListItem>
    )
  }

  // Rendering function for automatic group share field
  const renderAutomaticGroupShareFieldObject = (
    automaticGroupShareFieldObj: any,
    listItemKey: number
  ) => {
    if (automaticGroupShareFieldObj.type === 'addAutomaticGroupShare') {
      return (
        <ListItem key={listItemKey}>
          <ListItemIcon>
            <Button
              startIcon={<AddRounded style={{ fontSize: '25px', color: 'GrayText' }} />}
              onClick={handleAddAutomaticGroupShare}
              sx={{ fontSize: '16px', color: 'black', ml: '4px' }}
              style={{ color: 'black', textTransform: 'none' }}
            >
              {t('sharepoint:content.label.addAutomaticShare')}
            </Button>
          </ListItemIcon>
        </ListItem>
      )
    }
    return (
      <ListItem key={listItemKey} sx={{ alignItems: 'start' }}>
        <ListItemIcon sx={{ marginTop: '0.6875rem' }}>
          <IconButton onClick={() => handleDeleteAutomaticGroupShare(automaticGroupShareFieldObj)}>
            <DeleteRounded />
          </IconButton>
        </ListItemIcon>
        <ListItemText>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <SearchSelectInput
                name={t('sharepoint:content.label.shareWith')}
                fullWidth
                value={
                  automaticGroupSharesFormInput[automaticGroupShareFieldObj.key]?.group ?? null
                }
                // exclude all contacts that are selected already
                options={contactGroupOptions.filter(
                  (option: SelectOption) =>
                    !Object.values(automaticGroupSharesFormInput)
                      .map(
                        (autoShare: { group: SelectOption; customInvitationMessage: string }) =>
                          autoShare.group
                      )
                      .includes(option)
                )}
                onChange={(input: any) => {
                  const fieldObjKey = automaticGroupShareFieldObj.key
                  log.debug('Updating', fieldObjKey, 'with value ->', input)
                  const selectedOption =
                    contactGroupOptions.find(
                      (option: SelectOption) =>
                        (typeof input === 'object' && input?.value === option.value) ||
                        input === option.value
                    ) || (typeof input === 'object' && input?.value ? input : null)

                  if (!selectedOption) {
                    log.warn('Could not find matching option for input:', input)
                    return
                  }

                  setAutomaticGroupSharesFormInput(prev => {
                    const newState = { ...prev }
                    if (!newState[fieldObjKey]) {
                      // first select change -> create input object
                      newState[fieldObjKey] = {
                        group: selectedOption,
                        customInvitationMessage: '',
                      }
                    } else {
                      newState[fieldObjKey] = { ...newState[fieldObjKey], group: selectedOption }
                    }
                    return newState
                  })
                }}
                onAddOption={() => {
                  setNewGroupReceiver(automaticGroupShareFieldObj.key)
                  setOpenContactGroupDrawer(true)
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={
                  (
                    automaticGroupSharesFormInput[automaticGroupShareFieldObj.key]
                      ?.customInvitationMessage ?? ''
                  ).length > 0
                    ? 'Personalisierte Einladungsnachricht' +
                      `    ${(automaticGroupSharesFormInput[automaticGroupShareFieldObj.key]?.customInvitationMessage ?? '').length}/500`
                    : 'Personalisierte Einladungsnachricht'
                }
                value={
                  automaticGroupSharesFormInput[automaticGroupShareFieldObj.key]
                    ?.customInvitationMessage ?? ''
                }
                disabled={!automaticGroupSharesFormInput[automaticGroupShareFieldObj.key]?.group}
                fullWidth
                multiline
                rows={2}
                error={
                  (
                    automaticGroupSharesFormInput[automaticGroupShareFieldObj.key]
                      ?.customInvitationMessage ?? ''
                  ).length > 500
                }
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  const fieldObjKey = automaticGroupShareFieldObj.key
                  const input = event.target.value
                  log.debug('Updating', fieldObjKey, 'with value ->', input)

                  setAutomaticGroupSharesFormInput(prev => {
                    prev[fieldObjKey] = { ...prev[fieldObjKey], customInvitationMessage: input }
                    return { ...prev }
                  })
                }}
              />
            </Grid>
          </Grid>
        </ListItemText>
      </ListItem>
    )
  }

  const renderManualShareFieldObject = (manualShareFieldObj: any, listItemKey: number) => {
    if (manualShareFieldObj.type === 'addManualShare') {
      return (
        <ListItem key={listItemKey}>
          <ListItemIcon>
            <Button
              startIcon={<AddRounded style={{ fontSize: '25px', color: 'GrayText' }} />}
              onClick={handleAddManualShare}
              sx={{ textTransform: 'none', fontSize: '16px', color: 'black', ml: '4px' }}
              style={{ color: 'black', textTransform: 'none' }}
            >
              {t('sharepoint:content.label.addManualShare')}
            </Button>
          </ListItemIcon>
        </ListItem>
      )
    }
    return (
      <ListItem key={listItemKey}>
        <ListItemIcon>
          <IconButton onClick={() => handleDeleteManualShare(manualShareFieldObj)}>
            <DeleteRounded />
          </IconButton>
        </ListItemIcon>
        <ListItemText>
          <TextField
            value={manualSharesFormInput[manualShareFieldObj.key]?.comment ?? ''}
            /* eslint-disable-next-line @typescript-eslint/no-unsafe-call */
            onChange={(event: any) => manualShareFieldObj.onChange(event.target.value)}
            /* eslint-disable-next-line jsx-a11y/no-autofocus */
            autoFocus
            fullWidth
            label={manualShareFieldObj.label}
            required={true}
          />
        </ListItemText>
      </ListItem>
    )
  }
  /* ------------------------- Render constants end ------------------------- */

  /* ----------------------- Pre render actions start ----------------------- */
  // Fetch articles using React Query
  const { data: articlesData } = useAllArticles()

  useEffect(() => {
    if (articlesData && !prevOpen && open) {
      setPrevOpen(true)
      articleStore.current = [...articleStore.current, ...articlesData]
      setArticleOptions(
        articleStore.current.map(a => ({ value: a.self, label: getArticleRepresentation(a) }))
      )
    }
  }, [articlesData, prevOpen, open])

  // make sure that the guest request provides FileInfo objects if there are any
  let guestRequestFiles: FileInfo[] = []
  if (selectedGuestRequest) {
    if (
      typeof selectedGuestRequest.files === 'string' ||
      typeof selectedGuestRequest.files === 'undefined'
    ) {
      throw new Error(
        `Guest request ${selectedGuestRequest.key} does not provide its files. The files ` +
          'of a guest request are required. Make sure to expand the "files" property when ' +
          'fetching the guest requests.'
      )
    }
    guestRequestFiles = selectedGuestRequest.files
    // initiate article file input state
    if (!articleFileInput)
      // we need to remove the self property as the file is supposed to be uploaded
      // as an ArticleFile; the self describes the GuestRequestFile instance.
      // Yet we preserve the Pk to uniquely identify the FileInfo object.
      setArticleFileInput(
        guestRequestFiles.map(file => ({
          ...file,
          key: getPKfromSelf(file.self as string),
          self: undefined,
          guestRequest: undefined,
        }))
      )
  }

  // get the selected articles by their self values
  const selectedArticle = articleStore.current.filter(
    a => a.self === formState.state.guestRequestArticle?.value
  )[0]
  /* ------------------------ Pre render actions end ------------------------ */

  log.debug(
    'formState ->',
    formState.state,
    ', formEditorInput ->',
    formEditorInput,
    ', feedbackFormOptions ->',
    feedbackFormOptions,
    ', contactStore ->',
    contactStore.current,
    ', contactOptions ->',
    contactOptions,
    ', automaticSharesForm ->',
    automaticSharesForm,
    ', automaticSharesFormInput ->',
    automaticSharesFormInput,
    ', manualSharesForm ->',
    manualSharesForm,
    ', manualSharesFormInput ->',
    manualSharesFormInput,
    ', articleFormInput ->',
    articleFormInput,
    ', articleFileInput ->',
    articleFileInput
  )

  log.debug('contactGroupStore ->', contactGroupStore)

  /* ------------------------------------------------------------------------ */
  /*                             Render Component                             */
  /* ------------------------------------------------------------------------ */

  const sharepointFormFields = GetConstant({
    name: 'sharepointFormFields',
    feedbackFormOptions,
    onAddFeedbackFormOption: () => setOpenFormEditorDrawer(true),
    ArticleListComponent: <ArticleListComponent />,
    ArticleDescriptionComponent: <ArticleDescriptionComponent />,
    DurationInputComponent: <DurationInputComponent />,
    DurationDescriptionComponent: <DurationDescriptionComponent />,
    t,
  }) as FormFieldType[]

  return (
    <>
      <MultilevelDrawer
        open={open}
        setOpen={setOpen}
        size="big"
        title={t('sharepoint:content.heading.shareArticles')}
        onClose={handleClose}
        customHeader={renderDrawerHeader()}
        customActions={renderDrawerActions()}
      >
        {activeStep === 0 && (
          <>
            <Typography variant="body1">
              {t('sharepoint:content.description.guestRequestSharepointHints.introduction')}
            </Typography>
            <List>
              {implicationsAndComments.map(
                (listItem: { content: JSX.Element; icon?: JSX.Element }, index: number) => {
                  return (
                    <ListItem alignItems="flex-start" key={index}>
                      <ListItemIcon>
                        {listItem.icon ?? <TipsAndUpdatesRounded color="info" />}
                      </ListItemIcon>
                      <ListItemText>{listItem.content}</ListItemText>
                    </ListItem>
                  )
                }
              )}
            </List>
          </>
        )}
        {activeStep === 1 && (
          <>
            <Box id="guestRequestToAnonymizedArticleHeader" mb={4}>
              <Grid container>
                <Grid item xs={3}>
                  <Typography variant="h6" fontWeight={600} mb="1.2rem">
                    {t('request:content.heading.guestRequest')}
                  </Typography>
                </Grid>
                <Grid item xs={1} textAlign="center">
                  <ArrowForwardRounded color="success" fontSize="large" />
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="h6" fontWeight={600} mb="1.2rem">
                    {t('item:content.label.anonymizedArticle')}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
            <Box id="guestRequestToAnonymizedArticleContent">
              <Grid container>
                <Grid item xs={3}>
                  <Stack direction="column" spacing={3} position="sticky" top="0px">
                    <InfoField
                      label={t('request:content.label.requestFrom')}
                      value={selectedGuestRequest.name}
                    />
                    <InfoField
                      label={t('request:content.label.description')}
                      value={selectedGuestRequest.message}
                    />
                    <InfoField
                      label={t('common:content.label.material')}
                      value={selectedGuestRequest.material}
                    />
                    <InfoField
                      label={t('request:content.label.quantity')}
                      value={selectedGuestRequest.quantity?.split(',').join(', ')}
                    />
                    {guestRequestFiles.length > 0 && (
                      <>
                        <FieldLabel value={t('common:content.label.files')} />
                        <List dense>
                          {guestRequestFiles.map(file => (
                            <ListItem key={file.name}>
                              <ListItemIcon>
                                <InsertDriveFileRounded />
                              </ListItemIcon>
                              {file.name}
                            </ListItem>
                          ))}
                        </List>
                      </>
                    )}
                  </Stack>
                </Grid>
                <Grid item xs={1}>
                  <Box border="1px solid lightgray" height="100%" width={0} mx="auto"></Box>
                </Grid>
                <Grid item xs={8}>
                  <Box>
                    <Typography variant="body1" mb={2}>
                      {t('item:content.heading.selectAnonymizedArticle')}
                    </Typography>
                    <SearchSelectInput
                      name={t('common:content.label.item')}
                      value={formState.state.guestRequestArticle}
                      options={articleOptions}
                      onChange={(input: any) => {
                        log.debug('selectedArticle ->', input)
                        formState.setState((prev: any) => ({
                          ...prev,
                          guestRequestArticle: input ?? undefined,
                        }))
                      }}
                    />
                    <Box display="flex" alignItems="center">
                      <Box borderBottom="1px solid lightgray" flexGrow={1}></Box>
                      <Typography
                        variant="body1"
                        fontWeight={600}
                        textAlign="center"
                        my={10}
                        mx={2}
                      >
                        {t('common:content.label.or')}
                      </Typography>
                      <Box borderBottom="1px solid lightgray" flexGrow={1}></Box>
                    </Box>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                      <Typography variant="body1">
                        {t('item:content.heading.createAnonymizedArticle')}
                      </Typography>
                      <Button
                        variant="contained"
                        size="small"
                        disabled={
                          !isValidArticleInput() || Boolean(formState.state.guestRequestArticle)
                        }
                        onClick={() => handleCreateAnonymizedArticle({ ...articleFormInput })}
                      >
                        {t('item:content.label.createArticle')}
                      </Button>
                    </Box>
                    <ArticleForm
                      disabled={typeof formState.state.guestRequestArticle === 'object'}
                      formState={{
                        state: articleFormInput,
                        setState: setArticleFormInput,
                      }}
                      inputFiles={{
                        state: articleFileInput ?? [],
                        setState: setArticleFileInput,
                      }}
                      internalComponentProps={{
                        inputFileList: {
                          columns: ['name', 'actions'],
                          onEditFile: file => {
                            const ext = getFileExtension(file.name as string)
                            if (ext && ['png', 'jpeg', 'jpg'].includes(ext)) {
                              setSelectedArticleFile(file)
                              setOpenImageEditor(true)
                            }
                          },
                        },
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </>
        )}
        {activeStep === 2 && (
          <>
            <Typography variant="h6" mb="1.2rem">
              {t('sharepoint:content.heading.scopeAndDuration')}
            </Typography>
            <Grid container spacing={2} pl="2rem">
              <Grid item xs={12}>
                <Form
                  editing={true}
                  formFields={sharepointFormFields.filter(
                    field =>
                      field.key === 'articleList' ||
                      field.key === 'articleDescription' ||
                      field.key === 'sharepointDuration' ||
                      field.key === 'durationDescription' ||
                      field.key === 'feedbackForm'
                  )}
                  formObject={formState.state ?? {}}
                  onChange={(value: FormObject) => formState.setState(value as any)}
                />
              </Grid>
              <Grid item xs={12} pb="1rem">
                <Accordion disableGutters>
                  <AccordionSummary expandIcon={<ExpandMoreRounded />}>
                    {t('sharepoint:content.heading.feedbackFormReview')}
                  </AccordionSummary>
                  <AccordionDetails>
                    {formState.state?.feedbackForm ? (
                      renderFeedbackForm(getFeedbackForm(formState.state?.feedbackForm?.value))
                    ) : (
                      <Typography
                        variant="body1"
                        color="GrayText"
                        textAlign="center"
                        width="100%"
                        mt="1.5rem"
                        mb="1.5rem"
                      >
                        {t('sharepoint:feedback.info.selectFeedbackForm')}
                      </Typography>
                    )}
                  </AccordionDetails>
                </Accordion>
              </Grid>
            </Grid>
            {/* TODO: feature for the future  */}
            {/* <Typography variant='h6' mt='4rem;' mb='1.2rem'>
            {t('common:content.label.security')}
          </Typography>
          <Grid container spacing={2} pl='2rem'>
            <Grid item xs={6}>
              <FieldLabel value={t('common:content.label.permissions')} />
              <FormControl
                fullWidth
                sx={{
                  border: '1px solid LightGrey',
                  borderRadius: '4px',
                  padding: '14px',
                  position: 'relative',
                }}
              >
                <FormGroup row>
                  <FormControlLabel
                    disabled
                    control={<Switch defaultChecked />}
                    label={t('sharepoint:interaction.switch.sharepointViewPermission')}
                  />
                  <FormControlLabel
                    control={<Switch />}
                    label={t('sharepoint:interaction.switch.sharepointEditPermission')}
                    name="edit"
                    checked={formState.state.permissions?.edit || false}
                    onChange={(e: any) => formState.setState((prev: any) => {return {
                        ...prev,
                        permissions: {
                          [e?.target.name]: e.target.checked,
                        }
                      }}
                    )}
                  />
                </FormGroup>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body1">
                {t('sharepoint:content.description.configureSharepoint.permissionHint')}
              </Typography>
            </Grid>
          </Grid> */}
          </>
        )}
        {activeStep === steps.length - 1 && (
          <>
            <Typography variant="h6" mb="1.2rem">
              Mit Kontaktgruppen teilen
            </Typography>
            <Grid container>
              <Grid item xs={12}>
                <List>
                  <List>
                    {/* eslint-disable-next-line @typescript-eslint/no-unsafe-call */}
                    {automaticGroupSharesForm.map(
                      (automaticGroupShareFieldObj: any, index: number) =>
                        renderAutomaticGroupShareFieldObject(automaticGroupShareFieldObj, index)
                    )}
                  </List>
                </List>
              </Grid>
            </Grid>
            <Typography variant="h6" mb="1.2rem" mt="2rem">
              Mit einzelnen Kontakten teilen
            </Typography>
            <Grid container>
              <Grid item xs={12}>
                <List>
                  {/* eslint-disable-next-line @typescript-eslint/no-unsafe-call */}
                  {automaticSharesForm.map((automaticShareFieldObj: any, index: number) =>
                    renderAutomaticShareFieldObject(automaticShareFieldObj, index)
                  )}
                </List>
              </Grid>
            </Grid>
            <Accordion sx={{ mt: '4rem' }} disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreRounded />}>
                <Typography variant="body1">
                  {t('sharepoint:content.heading.shareManually')}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container>
                  <Grid item xs={12}>
                    <Typography variant="body1" ml="16px">
                      {t('sharepoint:content.description.configureSharepoint.manualShareHint')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <List>
                      {/* eslint-disable-next-line @typescript-eslint/no-unsafe-call */}
                      {manualSharesForm.map((manualShareFieldObj: any, index: number) =>
                        renderManualShareFieldObject(manualShareFieldObj, index)
                      )}
                    </List>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </>
        )}
        {activeStep === steps.length && (
          <>
            <Typography variant="h6" mb="1.2rem">
              {t('sharepoint:content.heading.shareManually')}
            </Typography>
            <Grid container spacing={6} pl="2rem">
              {Object.values(manualSharesFormInput).map((manualShare: any, index: number) => {
                return (
                  <Grid item container spacing={1} key={index}>
                    <Grid item xs={12}>
                      <InfoField
                        label={t('common:content.label.comment')}
                        value={manualShare.comment}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <InteractiveTextDisplay
                        type="link"
                        label={t('sharepoint:content.label.sharepointLink')}
                        value={manualShare.accessUrl}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <InteractiveTextDisplay
                        type="password"
                        label={t('common:content.auth.password')}
                        value={manualShare.password}
                      />
                    </Grid>
                  </Grid>
                )
              })}
            </Grid>
          </>
        )}
      </MultilevelDrawer>
      <ArticleDrawer
        open={openArticleDrawer}
        setOpen={setOpenArticleDrawer}
        formState={{
          state: articleFormInput,
          setState: setArticleFormInput,
        }}
        enableEditMode={true} // this allows us to pass files into the drawer
        onCreateConfirm={handleAddArticleOption}
      />
      <FilesDrawer
        open={openFilesDrawer}
        setOpen={setOpenFilesDrawer}
        files={guestRequestFiles}
        title={t('request:content.heading.guestRequestFiles', {
          defaultValue: 'Guest Request Files',
        })}
      />
      {openFormEditorDrawer && (
        <FormEditorDrawer
          open={openFormEditorDrawer}
          setOpen={setOpenFormEditorDrawer}
          formTitleSuggestion="Report your offer"
          formFieldSuggestion={buildFeedbackFormSuggestion(
            selectedGuestRequest.quantity.split(','),
            uniqueFieldKey
          )}
          onConfirm={handleAddFormOption}
        />
      )}
      <ContactDrawer
        open={openContactDrawer}
        setOpen={setOpenContactDrawer}
        formState={{
          state: contactFormInput,
          setState: setContactFormInput,
        }}
        onConfirm={handleAddContactOption}
      />
      <ContactGroupDrawer
        open={openContactGroupDrawer}
        setOpen={setOpenContactGroupDrawer}
        formState={{
          state: contactGroupFormInput,
          setState: setContactGroupFormInput,
        }}
        onConfirm={handleAddContactGroupOption}
      />
      <ImageManipulationModal
        title={
          t('imageManipulation:content.heading.anonymizeDrawing') + `: ${selectedArticleFile?.name}`
        }
        file={selectedArticleFile}
        open={openImageEditor}
        setOpen={setOpenImageEditor}
        confirmButtonProps={{
          text: t('request:interaction.button.confirmAnonymization'),
          onConfirm: handleConfirmImageManipulation,
          disabled: false,
        }}
      />
    </>
  )
}
